/* global fetch */

import { API_CONFIG } from "../config/api.js";
import { isCredentialFailure } from "../services/authFailure.js";
import { notifyAuthFailure } from "../services/api.js";
import { getAuthTransportSnapshot, getRequestAuthHeaders } from "./session.js";

async function pairingRequest(path, userCode, confirmReassignment = false) {
  const authSnapshot = getAuthTransportSnapshot();
  const response = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...getRequestAuthHeaders("POST"),
    },
    body: JSON.stringify({
      user_code: String(userCode || "")
        .trim()
        .toUpperCase(),
      confirm_reassignment: Boolean(confirmReassignment),
    }),
  });
  let responseBody = {};
  try {
    responseBody = await response.json();
  } catch {
    // Error handling below provides a stable fallback for empty responses.
  }
  if (!response.ok) {
    if (isCredentialFailure(response.status, responseBody)) {
      notifyAuthFailure(authSnapshot);
    }
    const detail =
      responseBody?.detail && typeof responseBody.detail === "object" ? responseBody.detail : {};
    const error = new Error(
      detail.detail ||
        detail.error ||
        responseBody?.detail ||
        responseBody?.error ||
        "Pairing request failed"
    );
    error.code = detail.code || responseBody?.code || "PAIRING_FAILED";
    error.status = response.status;
    throw error;
  }
  return responseBody;
}

async function connectorRequest(path, method = "GET", payload = null, options = {}) {
  const authSnapshot = getAuthTransportSnapshot();
  const headers = {
    ...getRequestAuthHeaders(method),
  };
  if (payload !== null) headers["Content-Type"] = "application/json";
  const response = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method,
    credentials: "include",
    headers,
    body: payload === null ? undefined : JSON.stringify(payload),
    signal: options.signal,
    keepalive: Boolean(options.keepalive),
  });
  let responseBody = {};
  try {
    responseBody = await response.json();
  } catch {
    // Error handling below provides a stable fallback for empty responses.
  }
  if (!response.ok) {
    if (isCredentialFailure(response.status, responseBody)) {
      notifyAuthFailure(authSnapshot);
    }
    const detail =
      responseBody?.detail && typeof responseBody.detail === "object" ? responseBody.detail : {};
    const error = new Error(
      detail.detail ||
        detail.error ||
        responseBody?.detail ||
        responseBody?.error ||
        "Connector request failed"
    );
    error.code = detail.code || responseBody?.code || "CONNECTOR_REQUEST_FAILED";
    error.status = response.status;
    throw error;
  }
  return responseBody;
}

export function inspectConnectorPairing(userCode) {
  return pairingRequest("/auth/connectors/pairings/inspect", userCode);
}

export function approveConnectorPairing(userCode, confirmReassignment = false) {
  return pairingRequest("/auth/connectors/pairings/approve", userCode, confirmReassignment);
}

export function denyConnectorPairing(userCode) {
  return pairingRequest("/auth/connectors/pairings/deny", userCode);
}

export async function listConnectorInstallations() {
  const body = await connectorRequest("/auth/connectors/installations");
  return Array.isArray(body.installations) ? body.installations : [];
}

export function revokeConnectorInstallation(installationId) {
  return connectorRequest(
    `/auth/connectors/installations/${encodeURIComponent(installationId)}`,
    "DELETE"
  );
}

export function heartbeatConnectorPresence(payload, signal) {
  return connectorRequest("/auth/connectors/discovery/presence", "PUT", payload, { signal });
}

export function removeConnectorPresence(presenceId, { keepalive = false } = {}) {
  return connectorRequest(
    `/auth/connectors/discovery/presence/${encodeURIComponent(presenceId)}`,
    "DELETE",
    null,
    { keepalive }
  );
}

export function approveDiscoveredConnectorPairing(
  pairingId,
  presenceId,
  confirmReassignment = false
) {
  return connectorRequest(
    `/auth/connectors/pairings/${encodeURIComponent(pairingId)}/approve`,
    "POST",
    {
      presence_id: presenceId,
      confirm_reassignment: Boolean(confirmReassignment),
    }
  );
}

export function dismissDiscoveredConnectorPairing(pairingId, presenceId) {
  return connectorRequest(
    `/auth/connectors/pairings/${encodeURIComponent(pairingId)}/dismiss`,
    "POST",
    { presence_id: presenceId }
  );
}
